import { get } from 'lodash';
import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { TextInput } from '@hivebrite/design-system/Inputs';

import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import type { OnChangeSetting } from 'hb-builder/types/settings';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

interface TextSettingProps {
  dataTestId?: string;
  description?: string;
  disabled?: boolean;
  initialValue?: string;
  item: DocumentItem;
  maxLength?: number;
  onChange: OnChangeSetting;
  settingKey: string;
  placeholder?: string;
  title?: string;
  verticalAlign?: boolean;
  width?: number | string;
}

const TextSetting = ({
  dataTestId,
  description,
  disabled,
  initialValue,
  item,
  maxLength,
  onChange,
  placeholder,
  settingKey,
  title,
  verticalAlign,
  width,
}: TextSettingProps) => {
  const rawInitialValue = get(item, settingKey) || initialValue || '';

  // Text value can come from "content" key, outside of "attributes".
  const [value, setValue] = useState(
    maxLength ? rawInitialValue.substring(0, maxLength) : rawInitialValue,
  );

  const handleChange = (e) => {
    const newValue = e.target.value || '';

    if (maxLength && newValue.length > maxLength) {
      return;
    }

    setValue(newValue);

    onChange({
      id: item.id,
      settingKey,
      value: newValue,
    });
  };

  if (description || verticalAlign) {
    return (
      <WrapperWithDescription>
        {title && (
          <InputTitle
            boxProps={{ mb: description && 2 }}
            id="textSetting"
            label={title}
          />
        )}
        {description && <Box mb={2}>{description}</Box>}
        <TextInput
          dataTestId={dataTestId}
          disabled={disabled}
          id="textSetting"
          name="textSetting"
          onChange={handleChange}
          value={value}
          variant="small"
        />
      </WrapperWithDescription>
    );
  }

  return (
    <Wrapper>
      {title && (
        <InputTitle
          boxProps={{ mr: 3 }}
          id={`${title}-textSetting`}
          key={title}
          label={title}
        />
      )}
      {description && <Box mb={2}>{description}</Box>}
      <TextInput
        dataTestId={dataTestId}
        disabled={disabled}
        id={`${title}-textSetting`}
        name="textSetting"
        onChange={handleChange}
        value={value}
        variant="small"
        width={width || 155}
        placeholder={placeholder}
      />
    </Wrapper>
  );
};

export default TextSetting;

const WrapperWithDescription = styled(Box).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  mb: 4,
}))``;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;
